import { useState } from 'react';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import { reportsApi } from '@/services/api';
import { downloadCsv } from '@/utils/csv';
import PurchaseReportFilters from '../shared/PurchaseReportFilters';
import ReportDataTable, { columnsToCsvHeaders, rowsToCsvData } from '../shared/ReportDataTable';
import ReportLayout from '../shared/ReportLayout';
import ReportSummaryCards from '../shared/ReportSummaryCards';
import { fetchPurchaseReportExport, usePurchaseReport } from '../shared/usePurchaseReport';
import { supplierSummaryColumns, SupplierSummaryRow } from './columns';
import { formatMoney } from '@/utils/currency';

export default function SupplierPurchaseSummaryPage() {
  const dateRangeHook = useReportDateRange('This Month');
  const { from_date, to_date } = dateRangeHook;
  const [supplier, setSupplier] = useState('');
  const [supplierLabel, setSupplierLabel] = useState('');

  const params = { from_date, to_date, supplier: supplier || undefined };

  const { rows, total, page, setPage, pageSize, summary, isLoading } = usePurchaseReport({
    queryKey: 'supplierPurchaseSummary',
    params,
    fetcher: (p) => reportsApi.getSupplierPurchaseSummary(p),
  });

  const top = summary.top_supplier as { supplier_name?: string; net_purchase?: number } | undefined;

  const handleExport = async () => {
    const data = await fetchPurchaseReportExport(reportsApi.getSupplierPurchaseSummary, params);
    downloadCsv(
      `supplier-purchase-summary-${from_date}-${to_date}.csv`,
      columnsToCsvHeaders(supplierSummaryColumns),
      rowsToCsvData(data.rows as unknown as SupplierSummaryRow[], supplierSummaryColumns),
    );
  };

  return (
    <ReportLayout
      onExport={handleExport}
      exportDisabled={!total}
      isLoading={isLoading}
      isEmpty={!rows.length}
      page={page}
      total={total}
      pageSize={pageSize}
      onPageChange={setPage}
      filters={
        <PurchaseReportFilters
          dateRangeHook={dateRangeHook}
          supplier={supplier}
          supplierLabel={supplierLabel}
          onSupplierChange={(id, label) => {
            setSupplier(id);
            setSupplierLabel(label);
          }}
        />
      }
      summary={
        <ReportSummaryCards
          items={[
            {
              label: 'Top Supplier',
              value: top?.supplier_name ? `${top.supplier_name}` : '—',
            },
            {
              label: 'Highest Purchase',
              value: top ? `${formatMoney(top.net_purchase)}` : '—',
            },
            {
              label: 'Avg Purchase per Supplier',
              value: `${formatMoney(summary.average_purchase_per_supplier as number)}`,
            },
          ]}
          columns={3}
        />
      }
    >
      <ReportDataTable data={rows as never[]} columns={supplierSummaryColumns} />
    </ReportLayout>
  );
}
